import { useState } from 'react'

import ReviewMap from './how_it_works_assets/review-map.png'
import SetDocs from './how_it_works_assets/set-docs.png'
import ProtectAsset from './how_it_works_assets/protect-asset.png'
import ConnectIntegrations from './how_it_works_assets/connect-integrations.png'
import DisplayLabel from './how_it_works_assets/display-label.png'
import TrackAnalytics from './how_it_works_assets/track-analytics.png'

const STEP_ICONS = {
  review: (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M10 4H7a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h6" />
      <path d="M14 4h3a2 2 0 0 1 2 2v6" />
      <path d="M8 9h6M8 13h4" />
      <circle cx="16.5" cy="16.5" r="3.5" />
      <path d="M19.2 19.2 21 21" />
    </svg>
  ),
  set: (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M7 4h10a2 2 0 0 1 2 2v12a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2Z" />
      <path d="M8.5 9h7M8.5 12h7M8.5 15h5" />
    </svg>
  ),
  protect: (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="m12 3 7 3v5c0 4.2-2.7 8-7 10-4.3-2-7-5.8-7-10V6l7-3Z" />
      <path d="m9.5 12.5 1.8 1.8 3.7-4.3" />
    </svg>
  ),
  connect: (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <circle cx="6" cy="12" r="2.2" />
      <circle cx="18" cy="6" r="2.2" />
      <circle cx="18" cy="18" r="2.2" />
      <path d="m8 11 7.6-4M8 13l7.6 4" />
    </svg>
  ),
  display: (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <rect x="3" y="5" width="18" height="14" rx="2.5" />
      <path d="M3 9h18M7 14h5" />
      <path d="M15 13.5h3.5v3H15z" />
    </svg>
  ),
  track: (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M5 19V9M12 19V5M19 19v-7M3 19h18" />
    </svg>
  ),
}




const STEPS = [
  {
    id: 'review',
    number: '00',
    label: 'Review',
    title: 'Understand what matters',
    visualTitle: 'Navigate global AI, privacy & copyright rules',
    description:
      'Review rights, privacy, provenance, policies and legal documents across jurisdictions to surface the requirements relevant to the content and task at hand.',
    challenge: 'Rights, privacy and transparency requirements vary across documents and jurisdictions.',
    solution: 'Our agentic AI brings the relevant requirements together so teams can review them in context.',
    image: ReviewMap,
    imageAlt: 'Illustrative world map highlighting selected markets with AI transparency requirements.',
    imageCaption: 'Subject to change as regulations evolve. Next update due 1 November 2026. Sign up for updates.',
  },
  {
    id: 'set',
    number: '01',
    label: 'Set',
    title: 'Turn requirements into usable rules',
    visualTitle: 'Build or import your rulebook',
    description:
      'Create licences, policies, permissions, terms of use and other content-control documents through a no-code workflow.',
    challenge: 'Rules are difficult to create consistently and even harder to operationalise.',
    solution: 'Create or bring in the policies, licences, terms and usage rules that define how your content can be used. TikBox turns them into structured rules that can be applied consistently.',
    image: SetDocs,
    imageAlt: 'Stacked document illustration representing licences, policies, terms and usage rules.',
  },
  {
    id: 'protect',
    number: '02',
    label: 'Protect',
    title: 'Credentials that stay with the asset',
    visualTitle: 'Keep provenance and rights data recoverable',
    description:
      'Attach C2PA/CAWG provenance and rights information, supported by watermarking and optional fingerprinting.',
    challenge: 'Metadata can be stripped by platforms, exports and processing, while crops can break simple asset references.',
    solution: 'Layer C2PA/CAWG with watermarking and fingerprinting to help recover the associated rights record.',
    image: ProtectAsset,
    imageAlt: 'Digital asset illustration showing a watermark and a C2PA or CAWG provenance badge.',
  },
  {
    id: 'connect',
    number: '03',
    label: 'Connect',
    title: 'Your systems, connected ',
    visualTitle: 'Add rights & compliance data to your systems',
    description:
      'Connect permissions, policy links and rights metadata with AEM, Salesforce and DAM workflows through TikBox connectors and APIs.',
    challenge: 'Rights information and new demands for provinance details often sits outside everyday content workflows.',
    solution: 'Bring it into the systems where teams already manage and distribute content.',
    image: ConnectIntegrations,
    imageAlt: 'Integration diagram connecting TikBox to AEM, Salesforce, DAM systems and APIs.',
  },
  {
    id: 'display',
    number: '04',
    label: 'Display',
    title: 'Show people when AI was used',
    visualTitle: 'Visible AI labels for EU AI Act transparency',
    description:
      'Carry provenance and disclosure information through to publishing and distribution workflows so these signals can remain visible to users.',
    challenge: 'Machine-readable provenance isn’t always enough. Some AI content must also be visibly disclosed to users under the EU AI Act.',
    solution: 'TikBox supports publishers in displaying relevant AI and provenance information alongside content.',
    image: DisplayLabel,
    imageAlt: 'Publisher webpage illustration showing a visible Made with AI label on an image.',
  },
  {
    id: 'track',
    number: '05',
    label: 'Track',
    title: 'Track the signals TikBox can verify',
    visualTitle: 'Tracking and reporting at a glance',
    description:
      'Use TikBox analytics to understand activity inside the rights workflows.',
    challenge: 'Teams need reliable evidence of what was requested, granted, validated or changed.',
    solution: 'See licence requests and completions, manifest validations and suspected alterations in one place.',
    image: TrackAnalytics,
    imageAlt: 'Rights analytics illustration showing licence requests, completions, validations and suspected alterations.',
  },
]

const HowItWorks = () => {
  const [activeId, setActiveId] = useState(STEPS[0].id)
  const activeStep = STEPS.find((step) => step.id === activeId) || STEPS[0]
  const activeIndex = Math.max(0, STEPS.findIndex((step) => step.id === activeId))
  const railProgress =
  [36, 49, 62, 75, 88, 100][activeIndex]

  const selectStep = (id) => setActiveId(id)

  const handleKeyDown = (event, index) => {
    if (!['ArrowDown', 'ArrowUp', 'Home', 'End'].includes(event.key)) return

    event.preventDefault()

    let nextIndex = index
    if (event.key === 'ArrowDown') nextIndex = (index + 1) % STEPS.length
    if (event.key === 'ArrowUp') nextIndex = (index - 1 + STEPS.length) % STEPS.length
    if (event.key === 'Home') nextIndex = 0
    if (event.key === 'End') nextIndex = STEPS.length - 1

    const nextStep = STEPS[nextIndex]
    selectStep(nextStep.id)
    document.getElementById(`how-it-works-tab-${nextStep.id}`)?.focus()
  }

  return (
    <section
      className="tikbox-how-it-works"
      id="how-it-works"
      aria-labelledby="how-it-works-title"
    >
      <div className="tikbox-how-it-works__container">
        <header className="tikbox-how-it-works__header">
          <p className="tikbox-how-it-works__eyebrow">How it works</p>
          <h2 id="how-it-works-title">
            From asset to governed, protected, licensable, displayed and tracked - in one connected flow
          </h2>
          <p>
            TikBox helps you understand what applies, put those requirements into practice and keep the resulting permissions, provenance, and transparency information connected to the content.
          </p>
        </header>

        <div className="tikbox-how-it-works__layout">
          <nav className="tikbox-how-it-works__steps" aria-label="TikBox rights workflow">
            <div className="step-menu" role="tablist" aria-orientation="vertical">
{/* Mess / tangle above the workflow */}
{/* Mess / tangle above the workflow */}
<svg
  className="step-tangle"
  viewBox="0 0 96 74"
  aria-hidden="true"
  focusable="false"
>
  <g
    fill="none"
    stroke="#25E4D1"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    vectorEffect="non-scaling-stroke"
  >
    {/* Upper knot */}
    <path
      d="
        M12 28

        C20 14 38 10 54 16
        C69 22 74 38 63 47
        C52 56 31 52 21 44
        C11 36 8 32 12 28

        C16 21 26 18 37 20
        C51 22 60 14 68 10
        C78 6 89 12 90 24
        C91 36 80 44 69 43
        C58 42 53 34 55 27
        C57 21 64 18 71 20
      "
    />

    {/* Lower left / lower centre loop */}
    <path
      d="
        M10 46

        C15 39 26 38 35 42
        C45 47 46 57 40 63
        C33 69 20 69 13 63
        C7 57 6 50 10 46

        C15 41 24 42 31 47
        C39 53 47 56 57 55
        C67 54 75 60 75 69
      "
    />

    {/* Diagonal crossover loop */}
    <path
      d="
        M28 60

        C22 52 23 42 30 36
        C37 30 47 31 53 37
        C59 43 58 52 51 57
        C44 62 34 65 28 60

        C37 64 49 63 58 58
        C66 54 72 47 71 39
        C70 31 63 27 56 28
        C48 29 45 35 46 41
      "
    />

    {/* Exit strand into the rail */}
    <path
      d="
        M75 69
        C74 71 72 73 70 74
        C66 76 61 75 58 72
      "
    />
  </g>
</svg>


{/* Process line */}
<svg
  className="step-rail"
  viewBox="0 0 70 1000"
  preserveAspectRatio="none"
  aria-hidden="true"
  focusable="false"
>
  <path
    className="step-rail__base"
    pathLength="100"
    d="
      M30 0

      C24 28 24 52 30 76
      C36 100 36 124 30 148

      C24 172 24 196 30 220
      C36 244 36 268 30 292

      C25 316 25 340 30 364
      C35 388 35 412 30 436

      C27 458 27 482 30 506

      L30 948
    "
  />

  <path
    className="step-rail__active"
    pathLength="100"
    strokeDasharray={`${railProgress} 100`}
    d="
      M30 0

      C24 28 24 52 30 76
      C36 100 36 124 30 148

      C24 172 24 196 30 220
      C36 244 36 268 30 292

      C25 316 25 340 30 364
      C35 388 35 412 30 436

      C27 458 27 482 30 506

      L30 948
    "
  />

  <path
    className="step-rail__arrow"
    d="M30 972 L19 958 M30 972 L41 958"
  />
</svg>
              {STEPS.map((step, index) => {
                const isActive = step.id === activeId

                return (
                  <button
                    key={step.id}
                    id={`how-it-works-tab-${step.id}`}
                    className={`step-menu__item${isActive ? ' is-active' : ''}`}
                    type="button"
                    role="tab"
                    aria-selected={isActive}
                    aria-controls={`how-it-works-panel-${step.id}`}
                    tabIndex={isActive ? 0 : -1}
                    onMouseEnter={() => selectStep(step.id)}
                    onFocus={() => selectStep(step.id)}
                    onClick={() => selectStep(step.id)}
                    onKeyDown={(event) => handleKeyDown(event, index)}
                  >
                    <span className="step-marker" aria-hidden="true">
                      {STEP_ICONS[step.id]}
                    </span>
                    <span className="step-menu__copy">
                      <span className="step-menu__meta">
                        <span className="step-number">{step.number}</span>
                        <span className="step-label">{step.label}</span>
                      </span>
                      <span className="step-menu__title">{step.title}</span>
                      <span className="step-menu__description">{step.description}</span>
                    </span>
                  </button>
                )
              })}
            </div>
          </nav>

          

          <div className="tikbox-how-it-works__card-stage">
            {STEPS.map((step) => {
              const isActive = step.id === activeId

              return (
                <article
                  key={step.id}
                  id={`how-it-works-panel-${step.id}`}
                  className={`workflow-card${isActive ? ' is-active' : ''}`}
                  role="tabpanel"
                  aria-labelledby={`how-it-works-tab-${step.id}`}
                  hidden={!isActive}
                >
                  <header className="workflow-card__header">
                    <div className="workflow-card__meta">
                      <span className="workflow-card__number">{step.number}</span>
                      <span className="workflow-card__label">{step.label}</span>
                    </div>
                    <h3>{step.visualTitle}</h3>
                  </header>

                  <figure className="workflow-card__visual">
                    <img
                      src={step.image}
                      alt={step.imageAlt}
                      loading="lazy"
                      decoding="async"
                      width="560"
                      height="320"
                    />
                    {step.imageCaption && <figcaption>{step.imageCaption}</figcaption>}
                  </figure>

                  <div className="workflow-card__statements">
  <div className="workflow-card__statements">
  <div className="workflow-card__statement workflow-card__statement--challenge">
    <div className="statement-heading">
      <span className="statement-icon" aria-hidden="true">!</span>
      <span className="statement-kicker">The challenge</span>
    </div>

    <p>{step.challenge}</p>
  </div>

  <div className="workflow-card__statement workflow-card__statement--solution">
    <div className="statement-heading">
      <span className="statement-icon" aria-hidden="true">✓</span>
      <span className="statement-kicker">How TikBox helps</span>
    </div>

    <p>{step.solution}</p>
  </div>
</div>
                  </div>
                </article>
              )
            })}

            

            <p className="workflow-card__status" aria-live="polite">
              Step {activeStep.number} of {String(STEPS.length - 1).padStart(2, '0')}
            </p>
          </div>
        </div>
      </div>
    </section>
  )
}

export default HowItWorks
